import React from "react";
import { useMutation, useQuery, useQueryCache } from "react-query";
import { useLocation, useParams } from "react-router-dom";
import ApiClient from "../utils/api-client";
import { Project } from "../utils/api/types";
import apiClient from "../utils/api-client";
import { overviewListQueryOptions } from "../utils/overview-query";
import { projectIdFromPathname, usableProjectId } from "../utils/project-id";

export type ProjectsQueryStatus = "loading" | "success" | "error";

/**
 * A failed or non-list response is not an empty account. Callers that treat
 * an error as [] show the "create your first project" screen and look like
 * the projects were deleted.
 */
export function projectsFromQuery(
  status: string,
  data: unknown
): { data: Project[]; status: ProjectsQueryStatus } {
  if (status === "success" && Array.isArray(data)) {
    return {
      data: data.filter((project) => usableProjectId(project?.id) !== null),
      status: "success",
    };
  }
  if (status === "loading" || status === "idle") {
    return { data: [], status: "loading" };
  }
  return { data: [], status: "error" };
}

const defaultEmptyProject: Project = {
  id: "",
  created_at: "",
  updated_at: "",
  name: "",
  country: "",
  brand_queries: [],
  properties: [],
  google_authentication_id: "",
  account_id: "",
  created_by: "",
  google_auth: {
    id: "",
    created_at: "",
    updated_at: "",
    email: "",
    access_token: "",
    refresh_token: "",
    expires_at: "",
  },
  tags: [],
  customer_journey: [],
  key_map: "",
  keymap_status: 0,
  user_agent: ""
};
export default function useProjects() {
  const { data, status } = useQuery("projects", async () => {
    const { data } = await ApiClient.getProjects();
    return data;
  }, overviewListQueryOptions);
  // No placeholder project while loading. An empty id was linked as " ",
  // which the browser opens as /%20/.
  return projectsFromQuery(status, data);
}

interface CurrentProjectContextProps {
  setCurrentProjectId: (projectId: string) => void;
  project: Project | null;
}
const CurrentProjectContext = React.createContext<CurrentProjectContextProps>({
  setCurrentProjectId: (projectId: string) => { },
  project: null,
});
CurrentProjectContext.displayName = "CurrentProjectContext";

function projectIdentity(project: Project) {
  return [
    project.id,
    project.key_map,
    project.updated_at,
    (project.brand_queries || []).join("|"),
  ].join(":");
}

/** Drop one project from a list cache. A non-list value is left unchanged. */
export function omitProject<T>(current: T, projectId: string): T {
  if (!Array.isArray(current)) {
    return current;
  }
  return current.filter((item) => {
    if (!item || typeof item !== "object") {
      return true;
    }
    return (item as { id?: string }).id !== projectId;
  }) as T;
}

/**
 * The project to show after the list changes. A project id from the page URL
 * wins, so reloading a check keeps that project. Otherwise a missing current
 * project becomes the first remaining one, or the empty project when none
 * remain. The same object is kept when the saved fields have not changed.
 */
export function nextCurrentProject(
  current: Project,
  projects: Project[],
  preferredId?: string | null
): Project {
  const usable = projects.filter((item) => usableProjectId(item.id) !== null);
  const preferred = usableProjectId(preferredId);
  const fromUrl = preferred ? usable.find((item) => item.id === preferred) : undefined;
  const selected = fromUrl ?? current;
  if (usableProjectId(selected.id) === null) {
    return usable[0] ?? defaultEmptyProject;
  }
  const fresh = usable.find((item) => item.id === selected.id);
  if (!fresh) {
    return usable[0] ?? defaultEmptyProject;
  }
  if (projectIdentity(fresh) === projectIdentity(selected)) {
    return selected === current ? current : fresh;
  }
  return fresh;
}

function CurrentProjectProvider(props: any) {
  const { pathname } = useLocation();
  const urlProjectId = projectIdFromPathname(pathname);
  const [currentProject, setCurrentProject] = React.useState<Project>(
    defaultEmptyProject
  );
  const { data: projects, status } = useProjects();
  const projectStamp = projects.map(projectIdentity).join(";");
  // Keep the project from the URL when a check (or any project page) is
  // reloaded. Otherwise pick the first project once, then keep the selected
  // project in step with later saves. key_map is set when a keyword map is
  // created; without this refresh the navigation still opens the create form.
  // A successful empty list clears a project that was just deleted. A loading
  // list does not.
  React.useEffect(() => {
    if (status !== "success") {
      return;
    }
    setCurrentProject((current) => nextCurrentProject(current, projects, urlProjectId));
  }, [projectStamp, projects, status, urlProjectId]);

  // A missing id used to be stored as the empty project. Every module link
  // then pointed at /%20/, and the lists for that id hid the real reports.
  const setCurrentProjectId = React.useCallback((projectId: string) => {
    const id = usableProjectId(projectId);
    if (!id) {
      return;
    }
    const next = projects.find((item) => item.id === id);
    if (!next) {
      return;
    }
    setCurrentProject((current) => (current === next ? current : next));
  }, [projects]);

  const value = React.useMemo(
    () => ({ project: currentProject, setCurrentProjectId }),
    [currentProject, setCurrentProjectId]
  );

  return (
    <CurrentProjectContext.Provider
      value={value}
      {...props}
    />
  );
}

function useCurrentProject() {
  const context = React.useContext(CurrentProjectContext);
  if (context === undefined) {
    throw new Error(
      "useCurrentProject must be used within an CurrentProjectProvider"
    );
  }
  return { ...context };
}

function useSetCurrentProjectFromURL() {
  const { projectId } = useProjectIdFromURL();
  const { setCurrentProjectId } = useCurrentProject();
  React.useEffect(() => {
    const id = usableProjectId(projectId);
    if (!id) {
      return;
    }
    setCurrentProjectId(id);
  }, [projectId, setCurrentProjectId]);
}

function useProjectIdFromURL() {
  const params = useParams<{ projectId: string }>();
  const { projectId } = params;
  return {
    projectId,
  };
}

function useProjectDelete() {
  const queryCache = useQueryCache();
  const [deleteProject] = useMutation(
    ({ projectId }: { projectId: string }) => {
      return apiClient.deleteProject({ projectId });
    },
    {
      throwOnError: true,
      onSuccess: async (_data, variables) => {
        queryCache.setQueryData("projects", (current) =>
          omitProject(current, variables.projectId)
        );
        // The delete already finished. A failed list refresh must not make
        // that success look like a failure.
        try {
          await queryCache.invalidateQueries("projects");
        } catch {
          return;
        }
      },
    }
  );
  return deleteProject;
}

function useDeleteKeywordMap() {
  const queryCache = useQueryCache();
  const [deleteKeywordMap] = useMutation(
    ({ projectId }: { projectId: string }) => {
      return apiClient.deleteKeywordMap({ projectId });
    },
    {
      onSuccess: async () => {
        await queryCache.invalidateQueries(["projects"]);
      },
    }
  );
  return deleteKeywordMap
}

export {
  useCurrentProject,
  CurrentProjectProvider,
  useSetCurrentProjectFromURL,
  useProjectIdFromURL,
  useProjectDelete,
  useDeleteKeywordMap,
};
